import axios from 'axios';
import { intersection } from 'lodash';
import mixpanel from 'mixpanel-browser';
import { useCallback, useContext, useState } from 'react';
import { useCookie } from 'react-use';

import {
  CTAInteractionTrackingEvents,
  InterestedOptions,
  TourCustomPageValue,
} from '@revelio/core';

import { environment } from '../../environment';
import { RedirectContext } from '../../redirect-context';
import { SurfContext } from '../../surf-context';
import { isE2eTestSignupEmail } from '../utils/is-e2e-test-signup-email';
import { personalizedReturnUrl } from '../utils/personalized-return-url';
import { AboutYouFields } from './about-you-fields';
import { ThemeHeading, ThemeTagline } from './theme/theme';
import { Stack, uiStyles } from './ui';

const interestedOptionsCusttomTourHandlingKeys = [
  InterestedOptions.Data_Enrichment_and_Custom_Reports,
  InterestedOptions.Talent_Acquisition,
];

const isLocalhost = window.location.host.startsWith('localhost');

function Step2({
  setActiveStep,
  formData,
}: {
  setActiveStep: (step: number) => void;
  formData: { name?: string; email: string; revelioId?: string };
}) {
  const [loading, setLoading] = useState(false);
  const [organization, setOrganization] = useState('');
  const [interest, setInterest] = useState('');
  const [submitError, setSubmitError] = useState('');
  const csrfToken = useContext(SurfContext);

  const [, updateCookie] = useCookie(TourCustomPageValue);

  const [redirect] = useContext(RedirectContext);

  // `personalized` is only true when the POST above actually succeeded — the
  // marker tells the dashboard the stamp has been written, so sending it after
  // a failed submit would skip a step the user still owes.
  const rerouteUser = useCallback(
    (personalized: boolean) => {
      const target = redirect || environment.PUBLIC_REACT_APP_URL;
      window.location.replace(
        personalized ? personalizedReturnUrl(target) : target
      );
    },
    [redirect]
  );

  const isOnlyAcademic = (value: string) =>
    value === InterestedOptions.Academic_Research;

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  const handleOnboarding = useCallback(async () => {
    setLoading(true);
    setSubmitError('');
    // Only a completed POST has written the stamp the dashboard reads.
    let personalized = false;
    try {
      if (formData.email) {
        const interestedIntersection = intersection<string>(
          interestedOptionsCusttomTourHandlingKeys,
          [interest]
        );
        if (interestedIntersection.length) {
          updateCookie(interestedIntersection.toString(), {
            domain: isLocalhost ? window.location.hostname : `reveliolabs.com`,
            expires: 30,
          });
        }
        // send new trial user and interested selections to crm. `userId` lets
        // user-mgmt stamp personalization_completed on the user's metadata so
        // the login gate stops routing them back here.
        await axios.post(
          `${environment.PUBLIC_USER_MGMT_URL}/api/users/interested-trial`,
          {
            email: formData.email,
            interests: [interest],
            company: organization.trim(),
            userId: formData.revelioId,
          },
          {
            timeout: 0,
            headers: {
              'x-surf-token': csrfToken,
            },
            withCredentials: true,
            xsrfCookieName: 'x-surf-token',
            xsrfHeaderName: 'x-surf-token',
          }
        );
        personalized = true;
        if (!isLocalhost && !isE2eTestSignupEmail(formData.email)) {
          mixpanel.track(CTAInteractionTrackingEvents.PROVIDE_ONBOARDING_INFO, {
            source: 'dashboard',
          });
        }
      }
    } catch (error) {
      // Leaving for the dashboard is only safe once the request confirms the
      // personalization gate is open — AuthGate sends an unstamped user to
      // /personalize, and each round trip re-sends the CRM lead. Stay put and
      // let them retry instead.
      console.error(error);
      setSubmitError('Something went wrong, please try again.');
      setLoading(false);
      return;
    }
    setLoading(false);
    if (isOnlyAcademic(interest)) {
      setActiveStep(3);
    } else {
      rerouteUser(personalized);
    }
  }, [
    csrfToken,
    updateCookie,
    formData.email,
    formData.revelioId,
    interest,
    organization,
    rerouteUser,
    setActiveStep,
  ]);

  return (
    <Stack gap="var(--ap-code-gap)" fullWidth>
      <Stack gap="35px">
        <ThemeHeading>Tell us a little about yourself</ThemeHeading>
        <ThemeTagline>
          Help us personalize your experience and recommend relevant workforce
          insights.
        </ThemeTagline>
      </Stack>
      <AboutYouFields
        organization={organization}
        onOrganizationChange={setOrganization}
        interest={interest}
        onInterestChange={setInterest}
        onContinue={handleOnboarding}
        loading={loading}
        disabled={!formData.email}
      />
      {submitError && <p className={uiStyles.formError}>{submitError}</p>}
    </Stack>
  );
}

export default Step2;
